import { Building2 } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { cn } from '@/lib/utils';

/**
 * Phase 2 Agency: client companies cap widget. Mirrors AgencyJobsCapWidget
 * shape so it slots into the dashboard "Usage this month" grid alongside
 * jobs/recruiters/transcripts. Reads from `me.agency`:
 *   used = agency.usage.clientsUsed
 *   cap  = agency.limits.agencyClients
 *
 * Renders nothing for non-agency users.
 */
export function AgencyClientsCapWidget() {
  const { user } = useAuth();
  const { t, language } = useLanguage();

  if (user?.accountType !== 'agency_owner' || !user.agency) return null;

  const used = user.agency.usage.clientsUsed ?? 0;
  const cap = user.agency.limits.agencyClients ?? 0;
  const pct = cap > 0 ? Math.min(100, Math.round((used / cap) * 100)) : 0;

  const tone =
    pct >= 90 ? 'bg-destructive' :
    pct >= 70 ? 'bg-warning' :
    'bg-brand-primary';

  const isIt = language === 'it';

  return (
    <Card className="p-4 border-border/60">
      <div className="flex items-start gap-3">
        <div className="h-9 w-9 rounded-md bg-brand-primary/10 text-brand-primary flex items-center justify-center flex-shrink-0">
          <Building2 className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs text-muted-foreground">
            {t('dashboard.agency.clientsCap') ?? (isIt ? 'Aziende cliente' : 'Client companies')}
          </div>
          <div className="mt-1 flex items-baseline gap-2">
            <span className="text-lg font-semibold text-foreground">{used}</span>
            <span className="text-sm text-muted-foreground">/ {cap}</span>
            <span className="text-xs text-muted-foreground ml-auto">{pct}%</span>
          </div>
          <div className="mt-2 h-1.5 w-full rounded-full bg-muted overflow-hidden">
            <div className={cn('h-full transition-all', tone)} style={{ width: `${pct}%` }} />
          </div>
        </div>
      </div>
    </Card>
  );
}

export default AgencyClientsCapWidget;
